import { MarkdownText } from '../markdown-text';
import { ModuleHistoryDialog } from './module-history-dialog';
import '../../assets/entity-list.css';
import '../../assets/module-details.css';
import { useContext, useMemo, useState } from "react";
import { ExplainedButton as Button } from '../explained-button';
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { HtmlViewer } from "../htmlViewer";
import { ModuleManagementDataContext, ModuleManagementDataContextValue } from '../../providers/module-management-provider';
import { ModuleMultiSelect } from '../module-multiselect';
import { ModuleFlow } from '../content-viewer/module-graph';
import { ModuleDto } from '../../general/dtos';
import { ViewManagedModuleDialog } from '../dialogs/view-managed-module-dialog';
import { EstimatedDurationField } from '../estimated-duration-field';
import { EstimatedDuration } from '../estimated-duration';

interface ModuleDetailsProps {
}

export function ModuleDetails(props: ModuleDetailsProps) {
    const ctx: ModuleManagementDataContextValue | null = useContext<ModuleManagementDataContextValue | null>(ModuleManagementDataContext);
    const { mode, selectedModuleLoading, selectedModules, selectedModuleFull, updateModuleField, updateModulePrerequisites,
        saveModule, cancelEditing, validationErrors, allModules } = ctx!;
    const [historyVisible, setHistoryVisible] = useState(false);
    const [dialogVisible, setDialogVisible] = useState<boolean>(false);

    // Prerequisite options (unchanged)
    const prerequisiteOptions = useMemo(() => {
        if (!ctx || !selectedModuleFull) return [];
        return allModules
            .filter(c => c.module_key !== selectedModuleFull.module_key)
            .map(c => ({
                label: c.name,
                value: c.module_key,
                managed: c.is_managed
            }));
    }, [ctx, selectedModuleFull, allModules]);

    if (selectedModuleLoading) {
        return <div style={{ padding: 16 }}>Loading...</div>;
    }

    if (selectedModules.length > 1) {
        return <div style={{ padding: 16 }}>Multiple modules selected</div>;
    }

    if (selectedModules.length === 0 && mode !== 'create') {
        return <div style={{ padding: 16 }}>No module selected</div>;
    }

    const history = <>
        <Button label="History" icon="pi pi-history" className="p-button-text" disabled={!selectedModuleFull?.module_key}
            disabledReason="Save the module once to start its revision history."
            title="Preview and restore saved module revisions" onClick={() => setHistoryVisible(true)} />
        {historyVisible && selectedModuleFull && <ModuleHistoryDialog key={selectedModuleFull.module_key} module={selectedModuleFull}
            onClose={() => setHistoryVisible(false)} onRestore={snapshot => { setHistoryVisible(false); ctx!.restoreRevision(snapshot); }} />}
    </>;

    const handleIframeMessage = (data: any) => {
        console.log(data); // nothing to handle in this place, there's no user interaction
    };

    function sanitizeFileName(name: string): string {
        return name
            .replace(/[^a-zA-Z0-9]/g, '-') // Replace everything not a letter/number with '-'
            .replace(/-+/g, '-')          // Collapse multiple consecutive hyphens into one
            .replace(/^-|-$/g, '');       // Trim hyphens from the start or end
    }

    function downloadJSON(data: ModuleDto, fileName: string = '') {
        const name: string = fileName.length > 0 ? fileName : `module-export-${sanitizeFileName(data.name)}.json`;

        // 1. Create a Blob from the JSON data
        const jsonString = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        
        // 2. Create a temporary URL representing the blob
        const url = URL.createObjectURL(blob);
        
        // 3. Create a temporary <a> tag
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        
        // 4. Programmatically click the link to trigger the download
        document.body.appendChild(link); // Required for Firefox
        link.click();
        
        // 5. Cleanup: remove the link and revoke the object URL
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    return (mode === "edit" || mode === "create") ? (<>
    <div className="module-editor">
    <div className="module-edit-details p-fluid">
      {/* NAME */}
      <div className="field module-edit-row">
        <label htmlFor="name">Name *</label>
        <InputText
          id="name"
          value={selectedModuleFull?.name ?? ''}
          maxLength={250}
          required
          onChange={(e) => updateModuleField("name", e.target.value)}
          className={validationErrors.name ? "p-invalid" : ""}
        />
        {validationErrors.name && <small className="p-error">{validationErrors.name}</small>}
      </div>

      <div className="field module-edit-row module-edit-description">
        <label htmlFor="description">Description</label>
        <InputTextarea
          id="description"
          value={selectedModuleFull?.description ?? ''}
          rows={2}
          maxLength={4094}
          onChange={(e) => updateModuleField("description", e.target.value)}
          className={validationErrors.description ? "p-invalid" : ""}
        />
        {validationErrors.description && <small className="p-error">{validationErrors.description}</small>}
      </div>

      <EstimatedDurationField compact value={selectedModuleFull?.estimated_minutes}
        onChange={value => updateModuleField('estimated_minutes', value)} error={validationErrors.estimated_minutes} />

      <div className="field module-edit-row">
        <label htmlFor="module-prerequisites">Pre-requisite modules</label>
          <ModuleMultiSelect
            inputId="module-prerequisites"
            value={selectedModuleFull?.prerequisites?.map(p => p.module_key)}
            options={prerequisiteOptions}
            onChange={updateModulePrerequisites}
            placeholder="Select prerequisite modules"
            className="w-full"
            display="chip"
          />
          {validationErrors.prerequisites && <small className="p-error">{validationErrors.prerequisites}</small>}
      </div>

      <div className="entity-list-container module-edit-content">
        <label htmlFor="content">Content *</label>
        <div className="entity-list-wrapper">
            <HtmlViewer
                htmlContent={selectedModuleFull?.content ?? ''}
                theme={selectedModuleFull?.theme ?? ''}
                instructions={selectedModuleFull?.instructions ?? ''}
                onMessage={(kind, data) => { alert("Message from iframe received: " + data); }}
                selectedMode={'text'}
                readOnly={false}
                compactControls
                onHtmlChanged={async (e) => updateModuleField("content", e)}
                onInstructionsChanged={async (e) => updateModuleField("instructions", e)}
                sandboxStyles={true}
            />
        </div>
        {validationErrors.content && <small className="p-error">{validationErrors.content}</small>}
        {validationErrors.instructions && <small className="p-error">{validationErrors.instructions}</small>}
      </div>

    </div>
    <div className="admin-toolbar p-3 border-top-1 surface-border flex justify-content-end gap-2">
        {!selectedModuleFull?.is_managed && history}
        <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={cancelEditing} />
        <Button label="Save" icon="pi pi-check" onClick={saveModule} />
    </div>
    </div>
</>) : (<>
    <div className="module-view-details p-fluid" style={{ padding: "0", display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="field">
        <div className="font-bold">Description:</div>
        <MarkdownText className="module-description-text" style={{ maxHeight: '12rem', overflowY: 'auto' }} text={selectedModuleFull?.description} />
        <EstimatedDuration minutes={selectedModuleFull?.estimated_minutes} />
      </div>
      { selectedModuleFull?.is_managed ? 
        <>
            <div className="font-bold">Module fragments:</div>
            <div className="admin-toolbar">
                <Button
                    label="View..."
                    icon="pi pi-play"
                    className={"p-button-text p-button-sm"}
                    onClick={() => setDialogVisible(true)}
                    style={{ width: "auto" }}
                />
                <Button
                    label="Export..."
                    icon="pi pi-download"
                    className={"p-button-text p-button-sm"}
                    onClick={() => downloadJSON(selectedModuleFull)}
                    style={{ width: "auto" }}
                />
            </div>
            <ModuleFlow module={selectedModuleFull as ModuleDto} />
        </> :
        <div className="entity-list-container">
            <div className="font-bold">Module content:</div>
            <div className="entity-list-wrapper" style={{ isolation: 'isolate' }}>
                <HtmlViewer
                    htmlContent={selectedModuleFull?.content ?? ''}
                    theme={selectedModuleFull?.theme ?? ''}
                    instructions={selectedModuleFull?.instructions ?? ''}
                    onMessage={(data) => { handleIframeMessage(data); }}
                    selectedMode={'preview'}
                    readOnly={true}
                    sandboxStyles={true}
                />
            </div>
        </div>
      }
    </div>

    <ViewManagedModuleDialog 
        contentLoading={false}
        fullModule={selectedModuleFull}
        onClose={() => setDialogVisible(false)}
        visible={dialogVisible}
        isTestMode={true}
    />
</>);
}
